import {
  Box,
  type BoxProps,
  Input,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type {
  ApiError,
  PbxSettingsApi,
  PhoneNumberListItem,
} from '../api/pbxSettingsApi.types'
import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'

interface PhoneNumberPage {
  count: number
  results: PhoneNumberListItem[]
}

const DEBOUNCE_DELAY_MS = 300
const INITIAL_PAGE_SIZE = 50
const SEARCH_INITIAL_PAGE_SIZE = 10
const NEXT_PAGE_SIZE = 15
const SEARCH_THRESHOLD = 50
const MENU_MAX_HEIGHT = '300px'
const PHONE_BATCH_TIMEOUT = 90_000
const inputFocusStyles = {
  bg: 'bg.subtle',
  borderColor: 'border.default',
  borderRadius: 'lg',
  _focusVisible: {
    borderColor: 'blue.500',
    boxShadow: '0 0 0 2px var(--chakra-colors-blue-500)',
  },
}

export interface PbxPhoneNumberSelectProps extends Omit<BoxProps, 'onChange'> {
  api: PbxSettingsApi
  t?: PbxSettingsTranslateFn
  pbxId: number
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  placeholder?: string
  sourceMode?: 'caller_id' | 'fax'
}

export const PbxPhoneNumberSelect = memo(
  ({
    api,
    t,
    pbxId,
    value,
    onChange,
    disabled = false,
    placeholder,
    sourceMode = 'caller_id',
    ...rest
  }: PbxPhoneNumberSelectProps) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [persistedSelectedPhone, setPersistedSelectedPhone] =
      useState<PhoneNumberListItem | null>(null)

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearch(searchTerm)
      }, DEBOUNCE_DELAY_MS)
      return () => clearTimeout(timer)
    }, [searchTerm])

    const {
      data: phonePages,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useInfiniteQuery<PhoneNumberPage, ApiError>({
      queryKey: ['phoneNumbers', 'infiniteSelect', pbxId, debouncedSearch],
      enabled: Boolean(pbxId) && isOpen,
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => {
        const offset =
          typeof pageParam === 'number'
            ? pageParam
            : Number.parseInt(String(pageParam), 10) || 0

        const firstPageSize = debouncedSearch
          ? SEARCH_INITIAL_PAGE_SIZE
          : INITIAL_PAGE_SIZE
        const limit = offset === 0 ? firstPageSize : NEXT_PAGE_SIZE
        const response = await api.getPhoneNumbers({
          pbx_id: pbxId,
          limit,
          offset,
          search: debouncedSearch || undefined,
          timeout: PHONE_BATCH_TIMEOUT,
        })

        if (!response.ok) {
          return Promise.reject(response.data as ApiError)
        }

        const data = response.data as
          { count?: number; results?: PhoneNumberListItem[] } | undefined
        return {
          count: data?.count ?? 0,
          results: data?.results ?? [],
        }
      },
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce(
          (sum, page) => sum + page.results.length,
          0,
        )
        return loaded < lastPage.count ? loaded : undefined
      },
      retry: 1,
    })

    const { data: unfilteredPhoneCount } = useQuery<number, ApiError>({
      queryKey: ['phoneNumbers', 'totalCount', pbxId],
      enabled: Boolean(pbxId),
      queryFn: async () => {
        const response = await api.getPhoneNumbers({
          pbx_id: pbxId,
          limit: 1,
          timeout: PHONE_BATCH_TIMEOUT,
        })
        if (!response.ok) {
          return Promise.reject(response.data as ApiError)
        }
        const data = response.data as { count?: number } | undefined
        return data?.count ?? 0
      },
      staleTime: 30 * 60 * 1000,
    })

    const { data: selectedPhoneFromApi } = useQuery<
      PhoneNumberListItem | null,
      ApiError
    >({
      queryKey: ['phoneNumbers', 'selectedNumber', pbxId, value],
      enabled: Boolean(pbxId) && Boolean(value),
      queryFn: async () => {
        const response = await api.getPhoneNumbers({
          pbx_id: pbxId,
          number: value,
          limit: 1,
          timeout: PHONE_BATCH_TIMEOUT,
        })

        if (!response.ok) {
          return Promise.reject(response.data as ApiError)
        }

        const data = response.data as
          { results?: PhoneNumberListItem[] } | undefined
        return data?.results?.[0] ?? null
      },
      staleTime: 30 * 60 * 1000,
    })

    const phoneNumbers = useMemo(() => {
      const items = phonePages?.pages.flatMap((page) => page.results) ?? []
      const rankFor = (item: PhoneNumberListItem) => {
        if (sourceMode === 'caller_id') {
          return item.type === 'caller_id' ? 0 : 1
        }
        return /fax/i.test(item.name) ? 0 : 1
      }

      return [...items].sort((a, b) => {
        const rankDiff = rankFor(a) - rankFor(b)
        if (rankDiff !== 0) return rankDiff
        return a.name.localeCompare(b.name)
      })
    }, [phonePages, sourceMode])

    const phoneLookup = useMemo(() => {
      const lookup: Record<string, PhoneNumberListItem> = {}
      phoneNumbers.forEach((item) => {
        lookup[item.number] = item
      })
      if (persistedSelectedPhone) {
        lookup[persistedSelectedPhone.number] = persistedSelectedPhone
      }
      return lookup
    }, [phoneNumbers, persistedSelectedPhone])

    const selectedPhone = useMemo(() => {
      if (!value) return undefined
      return phoneLookup[value] ?? selectedPhoneFromApi ?? undefined
    }, [phoneLookup, selectedPhoneFromApi, value])

    useEffect(() => {
      if (selectedPhone) {
        setPersistedSelectedPhone(selectedPhone)
      } else if (!value) {
        setPersistedSelectedPhone(null)
      }
    }, [selectedPhone, value])

    const showSearch = (unfilteredPhoneCount ?? 0) > SEARCH_THRESHOLD

    const inputValue = useMemo(() => {
      if (!value) return ''
      const resolved =
        selectedPhone?.number ?? persistedSelectedPhone?.number ?? value
      const name = selectedPhone?.name ?? persistedSelectedPhone?.name
      return name ? `${name} - ${resolved}` : resolved
    }, [
      persistedSelectedPhone?.name,
      persistedSelectedPhone?.number,
      selectedPhone?.name,
      selectedPhone?.number,
      value,
    ])

    const handleInputFocus = useCallback(() => {
      if (!disabled) {
        setIsOpen(true)
      }
    }, [disabled])

    const isFocusInsideSelect = useCallback((target: EventTarget | null) => {
      if (!target) return false
      const node = target as Node
      return Boolean(
        inputRef.current?.contains(node) || dropdownRef.current?.contains(node),
      )
    }, [])

    const handleInputBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (!isFocusInsideSelect(event.relatedTarget)) {
          setIsOpen(false)
        }
      },
      [isFocusInsideSelect],
    )

    const handleSearchBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (!isFocusInsideSelect(event.relatedTarget)) {
          setIsOpen(false)
        }
      },
      [isFocusInsideSelect],
    )

    const handleInputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          if (isOpen) {
            event.preventDefault()
            setIsOpen(false)
          }
          return
        }

        if (
          !isOpen &&
          (event.key === 'ArrowDown' ||
            event.key === 'Enter' ||
            event.key === ' ')
        ) {
          event.preventDefault()
          setIsOpen(true)
        }
      },
      [isOpen],
    )

    const handleSearchKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          setIsOpen(false)
        }
      },
      [],
    )

    useEffect(() => {
      if (!isOpen) return
      if (showSearch) {
        const timer = setTimeout(() => searchInputRef.current?.focus(), 0)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [isOpen, showSearch])

    useEffect(() => {
      if (!isOpen) return

      const handlePointerDown = (event: MouseEvent) => {
        const target = event.target as Node
        const clickedInput = inputRef.current?.contains(target)
        const clickedDropdown = dropdownRef.current?.contains(target)
        if (!clickedInput && !clickedDropdown) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handlePointerDown)
      return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [isOpen])

    const emptyStateText = useMemo(() => {
      if (isLoading) {
        return text(
          t,
          'pbxSettings.phoneSelect.empty.loading',
          'Loading phone numbers...',
        )
      }
      if (debouncedSearch) {
        return text(
          t,
          'pbxSettings.phoneSelect.empty.noResults',
          'No phone numbers found',
        )
      }
      return text(
        t,
        'pbxSettings.phoneSelect.empty.noData',
        'No phone numbers available',
      )
    }, [debouncedSearch, isLoading, t])

    const resolvedPlaceholder = useMemo(
      () =>
        placeholder ??
        text(
          t,
          'pbxSettings.phoneSelect.placeholder',
          'Select from the List...',
        ),
      [placeholder, t],
    )

    const handlePhoneSelected = useCallback(
      (item: PhoneNumberListItem) => {
        onChange?.(item.number)
        setPersistedSelectedPhone(item)
        setIsOpen(false)
        setSearchTerm('')
      },
      [onChange],
    )

    return (
      <Box position="relative" width="100%" {...rest}>
        <Input
          {...inputFocusStyles}
          ref={inputRef}
          value={inputValue}
          onClick={handleInputFocus}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          readOnly
          px={3}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={text(
            t,
            'pbxSettings.phoneSelect.ariaLabel',
            'Phone number selector',
          )}
          aria-autocomplete="list"
          pr={10}
        />
        <Box
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
          pointerEvents="none"
          color="fg.muted"
        >
          <ChevronDown size={16} />
        </Box>

        {isOpen && (
          <Box
            ref={dropdownRef}
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            width="100%"
            zIndex={1500}
            bg="bg.panel"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="lg"
            shadow="lg"
            maxHeight={MENU_MAX_HEIGHT}
            overflowY="auto"
            onScroll={(event) => {
              if (!hasNextPage || isFetchingNextPage) return
              const target = event.currentTarget
              const nearBottom =
                target.scrollTop + target.clientHeight >=
                target.scrollHeight - 12
              if (nearBottom) {
                void fetchNextPage()
              }
            }}
          >
            {showSearch && (
              <Box
                position="sticky"
                top={0}
                zIndex={1}
                bg="bg.panel"
                borderBottomWidth="1px"
                borderColor="border.default"
                p={2}
              >
                <Input
                  {...inputFocusStyles}
                  ref={searchInputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={text(
                    t,
                    'pbxSettings.phoneSelect.searchPlaceholder',
                    'Search phone numbers...',
                  )}
                  size="sm"
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                />
              </Box>
            )}

            {phoneNumbers.length === 0 ? (
              isLoading ? (
                <Box p={3}>
                  <VStack align="stretch" gap={2}>
                    <Skeleton height="16px" />
                    <Skeleton height="16px" />
                    <Skeleton height="16px" />
                  </VStack>
                </Box>
              ) : (
                <Box p={4} textAlign="center" color="fg.muted">
                  <Text fontSize="sm">{emptyStateText}</Text>
                </Box>
              )
            ) : (
              phoneNumbers.map((item) => (
                <Box
                  key={`${item.id}-${item.number}`}
                  px={4}
                  py={2}
                  cursor="pointer"
                  bg={value === item.number ? 'blue.50' : 'transparent'}
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => handlePhoneSelected(item)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Text fontWeight="medium">
                    {item.name} - {item.number}
                  </Text>
                </Box>
              ))
            )}

            {isFetchingNextPage && (
              <Box p={3}>
                <Skeleton height="16px" />
              </Box>
            )}
          </Box>
        )}
      </Box>
    )
  },
)
